<script setup lang="ts">
import { ref, computed, watch, nextTick, onBeforeUnmount } from "vue";
import { onClickOutside } from "@vueuse/core";

const emit = defineEmits(["close", "clear"]);

const props = defineProps<{
  keyName: string;
  label: string;
  showSearch: boolean;
  isVisible: boolean;
  direction?: string;
}>();

const panelRef = ref<HTMLElement | null>(null);
const searchQuery = ref("");
// Measured pixel offset that keeps the flyout on screen; Tailwind can't express a runtime value.
const fitStyle = ref<Record<string, string>>({});

onClickOutside(panelRef, () => emit("close"), {
  ignore: [".ignore-filter-icon-click", ".panel-ref"],
});

const hasSearch = computed(() => props.showSearch && searchQuery.value.trim().length > 0);

const onClear = () => {
  emit("clear");
  searchQuery.value = "";
};

const VIEWPORT_MARGIN = 8;

// The flyout hangs from its row's top, so on a short screen a low row runs it off the bottom:
// shift it up as far as needed (never past the top edge) and cap it to the viewport height.
const fitToViewport = async () => {
  fitStyle.value = {};
  await nextTick();
  const panel = panelRef.value;
  if (!panel) return;
  const rect = panel.getBoundingClientRect();
  const maxHeight = window.innerHeight - 2 * VIEWPORT_MARGIN;
  const height = Math.min(rect.height, maxHeight);
  const overflow = rect.top + height + VIEWPORT_MARGIN - window.innerHeight;
  const shift = Math.max(0, Math.min(overflow, rect.top - VIEWPORT_MARGIN));
  fitStyle.value = {
    ...(shift > 0 ? { top: `-${shift}px` } : {}),
    ...(rect.height > maxHeight ? { maxHeight: `${maxHeight}px` } : {}),
  };
};

const onViewportChange = (event: Event) => {
  // Scrolling the option list itself moves nothing the flyout is anchored to.
  if (event.type === "scroll" && panelRef.value?.contains(event.target as Node)) return;
  void fitToViewport();
};

watch(
  () => props.isVisible,
  (visible) => {
    if (visible) {
      void fitToViewport();
      window.addEventListener("resize", onViewportChange);
      window.addEventListener("scroll", onViewportChange, true);
    } else {
      searchQuery.value = "";
      fitStyle.value = {};
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("scroll", onViewportChange, true);
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  window.removeEventListener("resize", onViewportChange);
  window.removeEventListener("scroll", onViewportChange, true);
});
</script>

<template>
  <Transition :name="direction === 'right' ? 'fade-left' : 'fade-right'">
    <div
      v-if="isVisible"
      ref="panelRef"
      class="panel-ref rounded-8 bg-elevated z-dropdown absolute top-0 flex max-h-96 w-64 flex-col p-3 pb-3 ring-1 ring-slate-200"
      :class="direction === 'right' ? '-right-[262px]' : '-left-[262px]'"
      :style="fitStyle"
      @click.stop>
      <input
        v-show="showSearch"
        v-model="searchQuery"
        type="text"
        placeholder="Search"
        class="focus-input t-small rounded-8 mb-3 border border-slate-200 p-2 leading-none" />

      <div class="mx-3 my-2 flex items-center justify-between">
        <span class="t-caption-bold text-type-muted uppercase">{{ label }}</span>
        <button
          class="t-caption text-type-muted hover:text-type-default transition-colors"
          @click="onClear">
          Clear
        </button>
      </div>

      <div class="ignore-filter-icon-click overflow-y-auto">
        <slot
          :search="searchQuery"
          :has-search="hasSearch" />
      </div>
    </div>
  </Transition>
</template>
